// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/components/charts.tsx (COPY)
/**
 * The Growth Score's two charts, inline SVG on the design tokens.
 *
 * - DimensionRadar: the five scored dimensions on a pentagon, rings at
 *   25 / 50 / 75 / 100 with the 50 ring (the cohort median) dashed.
 * - DotStrip: one metric across the cohort, each dot a competitor and the
 *   larger one the brand, the median dashed.
 */
import { MAX_SCORE, SCORE_REFERENCE } from '@/contracts/onboarding-session'
import { dsVar } from '@/design-system/tokens'
import { useProgress } from './motion'
import { DIMENSION_LOOK } from './visuals'
import type { DimensionKey } from '../growth-score.types'

/** Rings at every quarter of the scale, plus the reference (the cohort median, dashed). */
const RINGS = [...new Set([MAX_SCORE / 4, SCORE_REFERENCE, (MAX_SCORE * 3) / 4, MAX_SCORE])].sort((a, b) => a - b)

export function DimensionRadar({ dimensions, reveal = false, delay = 0 }: { dimensions: { key: DimensionKey; label: string; score: number }[]; reveal?: boolean; delay?: number }) {
  // The shape grows out of the centre, the dots land, then the labels.
  const progress = useProgress(1400, delay, reveal)
  // Tight around the pentagon and its labels, so it reads at half a panel too.
  const W = 400
  const H = 250
  const cx = W / 2
  const cy = 128
  const R = 86
  const n = dimensions.length
  const point = (i: number, v: number) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n
    return [cx + Math.cos(a) * ((R * v) / MAX_SCORE), cy + Math.sin(a) * ((R * v) / MAX_SCORE)] as const
  }
  const polygon = (values: number[]) => values.map((v, i) => point(i, v).map((x) => x.toFixed(1)).join(',')).join(' ')
  // A zero would collapse onto the centre; keep the point visible.
  const shown = dimensions.map((d) => Math.max(d.score, 3) * progress)
  // With nothing scored there is nothing to draw (and no axis to divide the circle by).
  if (n === 0) return null

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="mx-auto h-auto w-full max-w-[380px]" role="img" aria-label={`Scores by dimension: ${dimensions.map((d) => `${d.label} ${d.score}`).join(', ')}. ${SCORE_REFERENCE} is the cohort median.`}>
      {RINGS.map((ring) => (
        <polygon
          key={ring}
          points={polygon(dimensions.map(() => ring))}
          fill="none"
          stroke={dsVar(ring === SCORE_REFERENCE ? 'color.text-3' : 'color.hairline')}
          strokeDasharray={ring === SCORE_REFERENCE ? '3 3' : undefined}
        />
      ))}
      {dimensions.map((d, i) => {
        const [x, y] = point(i, MAX_SCORE)
        return <line key={d.key} x1={cx} y1={cy} x2={x} y2={y} stroke={dsVar('color.hairline')} />
      })}
      <polygon points={polygon(shown)} fill={dsVar('color.ink-a05')} stroke={dsVar('color.text-2')} strokeWidth={1.5} strokeLinejoin="round" />
      {dimensions.map((d, i) => {
        const [x, y] = point(i, shown[i]!)
        const [lx, ly] = point(i, 122)
        const anchor = Math.abs(lx - cx) < 10 ? 'middle' : lx < cx ? 'end' : 'start'
        return (
          <g key={d.key}>
            <circle cx={x} cy={y} r={4.5 * Math.min(1, progress * 1.5)} fill={dsVar(DIMENSION_LOOK[d.key].color)} />
            <text x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle" className="text-chart-axis" fill={dsVar('color.text-2')} opacity={Math.min(1, Math.max(0, (progress - 0.45) / 0.4))}>
              {d.label} ·{' '}
              <tspan fill={dsVar('color.ink')} fontWeight={600}>
                {Math.round(d.score * progress)}
              </tspan>
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export function DotStrip({ values, you, median, unit, label }: { values: number[]; you: number; median: number; unit: string; label: string }) {
  const W = 600
  const P = 12
  const all = [...values, you]
  const lo = Math.min(...all)
  const hi = Math.max(...all)
  const x = (v: number) => P + ((v - lo) / (hi - lo || 1)) * (W - 2 * P)
  const med = Math.round(median)
  return (
    <svg viewBox={`0 0 ${W} 42`} className="mx-auto h-auto w-full max-w-[380px]" role="img" aria-label={`${label}: you ${you}${unit}, cohort median ${med}${unit}`}>
      <line x1={P} y1={18} x2={W - P} y2={18} stroke={dsVar('color.hairline')} strokeWidth={2} />
      <line x1={x(median)} y1={8} x2={x(median)} y2={28} stroke={dsVar('color.text-3')} strokeWidth={1.5} strokeDasharray="2 3" />
      {values.map((v, i) => (
        <circle key={i} cx={x(v).toFixed(1)} cy={18} r={4.5} fill={dsVar('color.ink-a20')} />
      ))}
      <circle cx={x(you)} cy={18} r={7} fill={dsVar('color.ink')} stroke={dsVar('color.paper')} strokeWidth={2.5} />
      <text x={x(median)} y={40} textAnchor="middle" className="text-chart-axis" fill={dsVar('color.text-2')}>
        median {med}
        {unit}
      </text>
    </svg>
  )
}
